import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { ITeamMember } from '../../../types/teamMember';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
// import { getTeamMembersAsync } from './getTeamMembers';

interface IUpdateRoleRequest {
  teamMember: string;
  role: string;
}

interface IUpdateRoleResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    role?: {
      id?: string;
      name?: string;
      slug?: string;
      description?: string;
      isDefault?: boolean;
    };
    outlets: {
      id?: string;
      name?: string;
      address?: string;
      city?: string;
      state?: string;
      isActive?: boolean;
      createdAt?: string;
      updatedAt?: string;
    }[];
    inviteStatus?: ITeamMember['inviteStatus'];
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IUpdateRoleResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const updateRoleAsync = createAsyncThunk(
  'roleUpdateToDev',
  async (
    values: IUpdateRoleRequest[],
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IUpdateRoleResponse>(
        `/team-members/update-role`,
        values,
        config
      );
      return data;
    } catch (err) {
      const error = err as AxiosError<IUpdateRoleResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'success',
          description: errorMessage,
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateRoleSlice = createSlice({
  name: 'teamMember/update',
  initialState,
  reducers: {
    resetRoleUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateRoleAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(updateRoleAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IUpdateRoleResponse;
      })
      .addCase(updateRoleAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IUpdateRoleResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetRoleUpdate } = updateRoleSlice.actions;

export default updateRoleSlice.reducer;
